import React, { useState, useEffect } from "react";
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	Typography,
	Box,
	Stack,
	Alert,
	IconButton,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { IconLogin, IconEye, IconX } from "@tabler/icons-react";
import { useThreadsAuth } from "../../hooks/useThreadsAuth";

/**
 * MagazineLoginPrompt
 * 매거진 페이지 접속 시 로그인 선택 팝업
 * @param {object} props
 * @param {boolean} props.open - 팝업 열림 상태
 * @param {function} props.onClose - 팝업 닫기 콜백
 * @param {boolean} [props.isMobile]
 */
const MagazineLoginPrompt = ({ open, onClose, isMobile }) => {
	const theme = useTheme();
	const { login, isLoggedIn } = useThreadsAuth();
	const [isLoggingIn, setIsLoggingIn] = useState(false);

	// 이미 로그인된 경우 팝업 닫기
	useEffect(() => {
		if (isLoggedIn && open) {
			onClose();
		}
	}, [isLoggedIn, open, onClose]);

	const handleLogin = async () => {
		try {
			setIsLoggingIn(true);
			// 현재 페이지 URL 저장 (리다이렉트 용)
			sessionStorage.setItem("magazine_return_url", window.location.href);
			await login();
		} catch (error) {
			console.error("로그인 실패:", error);
			setIsLoggingIn(false);
		}
	};

	const handleViewWithoutLogin = () => {
		// 로컬 스토리지에 선택 저장 (24시간)
		const skipUntil = Date.now() + 24 * 60 * 60 * 1000;
		localStorage.setItem("magazine_skip_login", skipUntil.toString());
		onClose();
	};

	const handleClose = () => {
		// 단순히 팝업만 닫기 (스킵 설정 저장하지 않음)
		onClose();
	};

	return (
		<Dialog
			open={open}
			onClose={handleClose}
			maxWidth="sm"
			fullWidth
			fullScreen={isMobile}
			PaperProps={{
				sx: {
					borderRadius: isMobile ? 0 : 2,
					p: isMobile ? 1 : 2,
				},
			}}
		>
			<DialogTitle
				sx={{
					textAlign: "center",
					pb: 1,
					fontFamily:
						'"source-han-serif-korean", "Noto Serif KR", "Noto Serif CJK KR", "Source Han Serif KR", serif',
					fontWeight: 900,
					fontSize: isMobile ? "1.5rem" : "2rem",
					position: "relative",
				}}
			>
				Monthly Threads
				<IconButton
					onClick={handleClose}
					sx={{
						position: "absolute",
						right: isMobile ? -8 : -16,
						top: "50%",
						transform: "translateY(-50%)",
						color: "text.secondary",
					}}
					size="small"
				>
					<IconX size={20} />
				</IconButton>
			</DialogTitle>

			<DialogContent>
				<Stack spacing={3}>
					<Typography
						variant="body1"
						align="center"
						sx={{
							color: "text.secondary",
							wordBreak: "keep-all",
							lineHeight: 1.6,
						}}
					>
						스레드 로그인으로 모든 콘텐츠를 자유롭게 즐기세요
					</Typography>

					<Stack spacing={2}>
						<Box
							sx={{
								p: 2,
								borderRadius: 1,
								backgroundColor: "primary.main",
								color: "white",
							}}
						>
							<Stack spacing={1}>
								<Typography variant="subtitle2" fontWeight={700}>
									로그인 시 이용 가능
								</Typography>
								<Typography variant="caption">
									• 모든 이미지 조회
									<br />
									• 댓글 및 상호작용 정보
									<br />
									• 전체 콘텐츠 접근
								</Typography>
							</Stack>
						</Box>

						<Box
							sx={{
								p: 2,
								borderRadius: 1,
								border: "1px solid",
								borderColor: "divider",
							}}
						>
							<Stack spacing={1}>
								<Typography variant="subtitle2" fontWeight={700}>
									미로그인 시 제한
								</Typography>
								<Typography variant="caption" color="text.secondary">
									• 텍스트 콘텐츠만 조회
									<br />
									• 이미지 블러 처리
									<br />
									• 일부 기능 제한
								</Typography>
							</Stack>
						</Box>
					</Stack>

					{isLoggingIn && (
						<Alert severity="info" sx={{ mt: 2 }}>
							스레드 로그인 페이지로 이동 중...
						</Alert>
					)}
				</Stack>
			</DialogContent>

			<DialogActions sx={{ p: 2, pt: 1 }}>
				<Stack spacing={2} sx={{ width: "100%" }}>
					<Button
						variant="contained"
						fullWidth
						size="large"
						startIcon={<IconLogin size={20} />}
						onClick={handleLogin}
						disabled={isLoggingIn}
						sx={{
							py: 1.5,
							fontWeight: 700,
							fontSize: isMobile ? "1rem" : "1.1rem",
						}}
					>
						스레드로 로그인
					</Button>

					<Button
						variant="outlined"
						fullWidth
						size="large"
						startIcon={<IconEye size={20} />}
						onClick={handleViewWithoutLogin}
						disabled={isLoggingIn}
						sx={{
							py: 1.5,
							fontWeight: 700,
							fontSize: isMobile ? "1rem" : "1.1rem",
							borderColor: "divider",
							color: "text.secondary",
							"&:hover": {
								borderColor: "text.secondary",
								backgroundColor: "action.hover",
							},
						}}
					>
						로그인 없이 보기
					</Button>

					<Typography
						variant="caption"
						align="center"
						color="text.disabled"
						sx={{ mt: 1 }}
					>
						로그인 없이 보기를 선택하면 24시간 동안 다시 묻지 않습니다
					</Typography>
				</Stack>
			</DialogActions>
		</Dialog>
	);
};

export default MagazineLoginPrompt;